import { useVirtualizer } from "@tanstack/react-virtual";
import { TrendingUp } from "lucide-react";
import React, { useMemo, useRef } from "react";

import { getEntityName, getTypeIcon } from "../utils";

import { formatRelativeTime } from "./utils";

import { Entity, EntityType } from "@/queries/folder-entities/types";
import { cn } from "@/lib/utils";
import { CommandGroup, CommandItem, CommandList } from "@/components/ui/command";

interface SearchResultsProps {
  groupedEntities: Record<string, Entity[]>;
  totalResults: number;
  onEntitySelect: (entity: Entity) => void;
}

interface SearchResultsHeaderProps {
  totalResults: number;
}

interface EntityTypeHeaderProps {
  entityType: string;
  count: number;
}

interface EntityItemProps {
  entity: Entity;
  onEntitySelect: (entity: Entity) => void;
}

const SearchResultsHeader: React.FC<SearchResultsHeaderProps> = ({ totalResults }) => (
  <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
    <span>{`${totalResults} result${totalResults !== 1 ? "s" : ""}`}</span>
    <div className="flex items-center gap-1">
      <TrendingUp className="h-3 w-3" />
      <span>Most recent</span>
    </div>
  </div>
);

const EntityTypeHeader: React.FC<EntityTypeHeaderProps> = ({ entityType, count }) => (
  <CommandGroup className="mb-2 border-t first:border-t-0">
    <div className="flex items-center gap-2 px-4 py-2">
      {getTypeIcon(entityType as EntityType)}
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {getEntityName(entityType as EntityType)}s ({count})
      </span>
    </div>
  </CommandGroup>
);

const EntityItem: React.FC<EntityItemProps> = ({ entity, onEntitySelect }) => (
  <CommandItem
    value={`${entity.name} ${entity.type}`}
    onSelect={() => onEntitySelect(entity)}
    className={cn(
      "group relative mx-2 flex cursor-pointer items-center gap-3 px-4 py-3",
      "transition-colors",
    )}
  >
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-muted/50 group-data-[selected=true]:bg-accent">
      {getTypeIcon(entity.type)}
    </div>

    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-sm font-medium">{entity.name}</span>
      {entity.path && entity.path.join("") !== entity.name && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {entity.path.map((segment, index) => (
            <React.Fragment
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              {index > 0 && <span className="opacity-40">/</span>}
              <span className="truncate transition-colors">{segment}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {entity.updated_at && (
        <span className="text-xs text-muted-foreground">
          {formatRelativeTime(entity.updated_at)}
        </span>
      )}
    </div>
  </CommandItem>
);

export const SearchResults: React.FC<SearchResultsProps> = ({
  groupedEntities,
  totalResults,
  onEntitySelect,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => {
    const result: (
      | { type: "header"; entityType: string; count: number }
      | { type: "entity"; entity: Entity }
    )[] = [];

    Object.entries(groupedEntities).forEach(([type, typeEntities]) => {
      result.push({
        type: "header",
        entityType: type,
        count: typeEntities.length,
      });
      typeEntities.forEach(entity => {
        result.push({ type: "entity", entity });
      });
    });

    return result;
  }, [groupedEntities]);

  const shouldVirtualize = items.length > 500;

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const item = items[index];
      if (item.type === "header") {
        return 40;
      }
      return 56;
    },
    overscan: 5,
    enabled: shouldVirtualize,
  });

  const renderItem = (item: (typeof items)[number], key: string | number) => {
    if (item.type === "header") {
      return (
        <React.Fragment key={key}>
          <EntityTypeHeader entityType={item.entityType} count={item.count} />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={key}>
        <EntityItem entity={item.entity} onEntitySelect={onEntitySelect} />
      </React.Fragment>
    );
  };

  return (
    <CommandList ref={parentRef} className="max-h-[400px] overflow-y-auto pb-2">
      {totalResults > 0 && <SearchResultsHeader totalResults={totalResults} />}

      {shouldVirtualize ? (
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map(virtualItem => {
            const item = items[virtualItem.index];

            return (
              <div
                key={String(virtualItem.key)}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {renderItem(item, String(virtualItem.key))}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {items.map((item, index) =>
            renderItem(
              item,
              item.type === "header"
                ? `header-${index}`
                : `entity-${item.entity.type}-${item.entity.name}-${index}`,
            ),
          )}
        </>
      )}
    </CommandList>
  );
};
